/**
 * @module builder
 */
import type { OptionsDocument, PresentationOption, QuoteDocument } from 'paintscout'

import get from 'lodash/get'
import uniq from 'lodash/uniq'
import { getQuoteType } from '../../../quote/util'
import { getDefaultPresentation } from '../get-default-presentation'

export function getPresentationOptions(args: {
  options: OptionsDocument
  quoteType?: string
  inactive?: boolean
  quote?: QuoteDocument
  v2?: boolean
}): PresentationOption[] {
  const { options, inactive, v2 } = args
  const optionsKey = v2 ? 'presentationOptionsV2' : 'presentationOptions'

  const presentationOptions = get(options.options, optionsKey, {
    order: [],
    values: {} as { [key: string]: PresentationOption },
    quoteTypeOrder: {} as { [key: string]: string[] }
  })

  const quoteType = getQuoteType(args)

  let order =
    presentationOptions.quoteTypeOrder && presentationOptions.quoteTypeOrder[quoteType]
      ? uniq([...presentationOptions.quoteTypeOrder[quoteType], ...presentationOptions.order])
      : uniq(presentationOptions.order)

  if (!order) {
    order = []
  }

  const r = order
    .map((orderItem: string) => {
      const item = presentationOptions.values[orderItem] as PresentationOption
      if (!item) {
        return null
      }

      // v2 docs have no int_ext field at all, so an absent quoteTypes means "all types"
      // rather than falling back to a legacy v1-only field.
      let quoteTypes
      if (item.quoteTypes) {
        quoteTypes = item.quoteTypes
      } else if (!v2) {
        quoteTypes = [get(item, 'int_ext.value')]
      } else {
        quoteTypes = []
      }

      const option: PresentationOption = {
        ...item,
        key: orderItem,
        quoteTypes
      }

      if (!inactive && item.active !== true && typeof item.active !== 'undefined') {
        return null
      } else if (inactive) {
        option.active = typeof item.active === 'undefined' ? true : !!item.active
      }

      if (
        !quoteType ||
        quoteType === 'all' ||
        quoteTypes.indexOf('all') > -1 ||
        quoteTypes.indexOf(quoteType) > -1 ||
        !quoteTypes.length //If no quoteTypes are selected, show this option.
      ) {
        return option
      }

      return null
    })
    .filter((item) => item)

  // Only include default if its not saved as a presentation already
  if (r.find((item) => item.key === 'default')) {
    return [...r]
  } else {
    return [getDefaultPresentation({ options }), ...r]
  }
}
